import type { Components } from "react-markdown";

import { CopyableCodeBlock } from "~/components/CopyableCodeBlock";
import { cn } from "~/lib/utils";

const defaultMarkdownComponents = {
  h1: ({ node, ...props }) => <h1 className="text-3xl font-semibold mt-2 mb-1" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-2xl font-semibold mt-2 mb-0.5" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-xl font-semibold mt-2 mb-0.5" {...props} />,
  h4: ({ node, ...props }) => <h4 className="text-lg font-semibold mt-2 mb-0.5" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-8 mt-0.5 mb-4" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-8 mt-0.5 mb-4" {...props} />,
  li: ({ node, ...props }) => <li className="" {...props} />,
  p: ({ node, ...props }) => <p className="mt-0.5 mb-4" {...props} />,
  a: ({ node, ...props }) => (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  ),
} as Partial<Components>;

export default defaultMarkdownComponents;

/**
 * Extended markdown components with styled code blocks (with copy button),
 * inline code, and table formatting. Builds on defaultMarkdownComponents.
 */
export const richMarkdownComponents: Partial<Components> = {
  ...defaultMarkdownComponents,
  a({ node, children, ...props }) {
    try {
      new URL(props.href ?? "");
      props.target = "_blank";
      props.rel = "noopener noreferrer";
    } catch (_e) {}
    return <a {...props}>{children}</a>;
  },
  pre: CopyableCodeBlock as Components["pre"],
  code({ node, className, children, ...props }) {
    const isInline = !className;
    return isInline ? (
      <code
        className="bg-gray-100 text-gray-800 rounded px-1.5 py-0.5 text-sm font-mono"
        {...props}
      >
        {children}
      </code>
    ) : (
      <code className={cn("font-mono", className)} {...props}>
        {children}
      </code>
    );
  },
  table({ node, ...props }) {
    return <table className="w-full border-collapse mb-4 text-sm" {...props} />;
  },
  thead({ node, ...props }) {
    return <thead className="border-b-2 border-gray-200" {...props} />;
  },
  th({ node, ...props }) {
    return <th className="text-left p-2 font-semibold" {...props} />;
  },
  td({ node, ...props }) {
    return <td className="p-2 border-b border-gray-100" {...props} />;
  },
};
